import type { Me, MySettings, SortTableId, TableSort } from '@myrewards/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { meQuery, mySettingsQuery, updateMySettings } from '../api';

/** What `useTableSort` takes from the person's settings for one table. */
export interface SavedSort {
  /**
   * The person's last sort of the table, only with Keep my sort (APP-149 D7); null otherwise, so
   * the table opens at its own order ("Start in each table’s own order").
   */
  saved: TableSort | null;
  /** Where a person's sort goes: saved for the table with Keep my sort, else nowhere. */
  onSorted: (sort: TableSort) => void;
}

const sameSort = (a: TableSort, b: TableSort): boolean => a.column === b.column && a.dir === b.dir;

/** The table's saved sort, only with Keep my sort. */
function savedOf(settings: MySettings | undefined, table: SortTableId | null): TableSort | null {
  if (table === null || settings?.tableSortMode !== 'keep') return null;
  return settings.tableSorts?.[table] ?? null;
}

/**
 * Preview as, either way (B4): previewing roles can't save, and while acting as someone their
 * tables aren't the tester's to change. Unknown until `me` is read, so nothing is written before.
 */
function previewing(me: Me | undefined): boolean {
  return me === undefined || me.preview !== null || me.actingAs != null;
}

/**
 * A table's saved sort (APP-149 D7, B2): with Keep my sort, the table opens the way the person last
 * sorted it, on any device, and each sort they make is saved for that table alone (audited as a
 * personal setting). With "Start in each table’s own order" nothing is read or written. A sort that
 * matches the saved one isn't written again. In Preview as nothing is written (B4): previewing
 * roles can't save, and while acting as someone a sort lasts the visit and never changes how
 * that person's tables open. Read through `mySettingsQuery`, which a route loader warms first so
 * the table opens at the saved sort, as `loaderPaging` does for rows per page. A table with no
 * key of its own (`null`) keeps no sort.
 */
export function useSavedSort(table: SortTableId | null): SavedSort {
  const queryClient = useQueryClient();
  const settings = useQuery(mySettingsQuery);
  // Read so the saved sort follows the settings as they load or change.
  const saved = savedOf(settings.data, table);

  const onSorted = useCallback(
    (sort: TableSort) => {
      // Read from the cache at the time of the sort, so two quick sorts see each other.
      if (table === null) return;
      if (previewing(queryClient.getQueryData(meQuery.queryKey))) return;
      const now = queryClient.getQueryData(mySettingsQuery.queryKey);
      if (now?.tableSortMode !== 'keep') return;
      const last = savedOf(now, table);
      if (last !== null && sameSort(last, sort)) return;
      const next = { column: sort.column, dir: sort.dir };
      // The cache moves at once, so the table's own fallback and the next sort both see this one.
      queryClient.setQueryData(mySettingsQuery.queryKey, {
        ...now,
        tableSorts: { ...now.tableSorts, [table]: next },
      });
      // Fire and forget, as rows per page are: if the save fails, the sort still lasts the visit.
      void updateMySettings({ tableSorts: { [table]: next } })
        .then((answer) => {
          queryClient.setQueryData(mySettingsQuery.queryKey, answer);
        })
        .catch(() => {
          void queryClient.invalidateQueries({ queryKey: mySettingsQuery.queryKey });
        });
    },
    [queryClient, table],
  );

  return { saved, onSorted };
}
